import { Divider, Stack, Typography } from "@mui/material";
import _ from "lodash";
import { get } from "radash";
import React, { Fragment, useEffect, useState } from "react";
import { Control, useFormContext } from "react-hook-form";

import {
  Checkbox,
  ConditionalResponse,
  DatePickerInput,
  DynamicAutocomplete,
  FileInput,
  Input,
  MultiSelect,
  MultiSelectResponse,
  NumericInput,
  Password,
  PatternInput,
  Radio,
  SelectInput,
  SwitchToggle,
  TextArea,
  ToggleButtons,
} from "@components/FormControl";
import {
  DefaultFieldWrapper,
  FieldWrapperProps,
} from "@components/PatientForms/common/FormFieldWrapper";
import { getFormErrorMessage } from "@components/PatientForms/common/schemaHelper";

import { FormField, GroupInputField } from "./types";
import { devOnlyLog } from "@utils/helpers";

type FormFieldComponentProps = {
  field: FormField;
  control: Control;
  FieldWrapper?: React.ComponentType<FieldWrapperProps>;
};

const renderDivider = () => <Divider sx={{ mx: "10px !important" }} />;

const useDynamicOnChange = (field: FormField) => {
  const { watch, getValues, setValue } = useFormContext(); // retrieve all hook methods
  const value = field.sideEffectOnChange ? watch(field.name) : undefined;

  useEffect(() => {
    if (value && field.sideEffectOnChange) {
      field.sideEffectOnChange(setValue, getValues());
    }
  }, [value]);
};

const FormFieldComponent = ({
  field,
  FieldWrapper = DefaultFieldWrapper,
}: FormFieldComponentProps): React.ReactElement | null => {
  const [isInitialized, setIsInitialized] = useState(false);
  const { watch, resetField, getValues, setValue, getFieldState, formState, control } =
    useFormContext(); // retrieve all hook methods

  // Log field errors (dev)
  const fieldError = get(formState.errors, field.name)
  if(fieldError) devOnlyLog({ fieldName: field.name, fieldError });


  useDynamicOnChange(field);

  const conditional = field.conditional;
  // Safeguard: Only watch the specific field if the label is defined
  const watchedValue = conditional?.label ? watch(conditional.label) : undefined;

  // Determine if the field should be rendered
  const conditionalValues = conditional?.values || [];
  const shouldRender = Array.isArray(watchedValue)
    ? watchedValue.some((value) => conditionalValues.includes(value))
    : conditionalValues.includes(watchedValue);

  // Initialize the value before rendering if needed
  const updateDefaultValueBeforeRender = (fieldObject: FormField & { group?: FormField[] }) => {
    const group = fieldObject.group?.length ? fieldObject.group : [fieldObject];
    const values = getValues();
    for (const field of group) {
      const currentValue = get(values, field.name);
      if (currentValue === undefined) {
        // Set the default value
        typeof field.defaultValue === "function"
          ? field.defaultValue(setValue, values)
          : setValue(field.name, field.defaultValue);
      }
    }

    setIsInitialized(true); // Mark as initialized
  };

  useEffect(() => {
    if (conditional?.label || field.defaultValue) {
      if (shouldRender) {
        updateDefaultValueBeforeRender(field);
      } else if (getFieldState(field.name).invalid || conditional?.resetOnHide) {
        resetField(field.name);
      }
    }

    // Reset initialization state if condition is not met
    if (!shouldRender) {
      setIsInitialized(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedValue]);

  // Render only if conditional value is met
  const renderConditionMet =
    field?.defaultValue !== undefined 
      ? !isInitialized || !shouldRender 
      : !shouldRender;

  if (conditional?.label && renderConditionMet) return null;

  const sx = get(field, "fieldProps.sx", {});
  const getFieldProps = (field: Exclude<FormField, GroupInputField>) => ({
    ...field.fieldProps,
    sx: { backgroundColor: "white", ...sx },
  });

  const getField = (field: FormField) => {
    let formField = null;
    const helperText = field.showHelperText
      ? getFormErrorMessage(formState, field.name)
      : undefined;

    switch (field.type) {
      case "input":
        formField = (
          <Input
            helperText={helperText}
            label={field.label}
            name={field.name}
            control={control}
            fullWidth
            icon={field.icon}
            {...getFieldProps(field)}
          />
        );

        break;
      case "password":
        formField = (
          <Password
            helperText={helperText}
            label={field.label}
            name={field.name}
            control={control}
            fullWidth
            {...getFieldProps(field)}
          />
        );

        break;
      case "numeric":
        formField = (
          <NumericInput
            helperText={helperText}
            label={field.label}
            name={field.name}
            control={control}
            fullWidth
            {...getFieldProps(field)}
          />
        );

        break;

      case "pattern":
        formField = (
          <PatternInput
            helperText={helperText}
            label={field.label}
            name={field.name}
            control={control}
            fullWidth
            {...getFieldProps(field)}
          />
        );

        break;

      case "textarea":
        formField = (
          <TextArea
            helperText={helperText}
            label={field.label}
            name={field.name}
            control={control}
            fullWidth
            {...getFieldProps(field)}
          />
        );

        break;

      case "date":
        formField = (
          <DatePickerInput
            helperText={helperText}
            label={field.label}
            name={field.name}
            control={control}
            {...getFieldProps(field)}
          />
        );

        break;

      case "select":
        let options = field.options || [];
        if (field.dynamicOptions) {
          options = getValues(field.dynamicOptions.name).map((option: any) => ({
            label: option[field.dynamicOptions?.labelField || "label"],
            value: option[field.dynamicOptions?.valueField || "value"],
          }));
        }

        formField = (
          <SelectInput
            label={field.label}
            name={field.name}
            control={control}
            fullWidth
            options={options}
            {...getFieldProps(field)}
          />
        );

        break;

      case "autocomplete":
        formField = (
          <DynamicAutocomplete
            helperText={helperText}
            label={field.label}
            name={field.name}
            control={control}
            fullWidth
            options={field.options || []}
            {...getFieldProps(field)}
          />
        );

        break;
      case "checkbox":
        formField = (
          <Checkbox
            key={field.name}
            name={field.name}
            control={control}
            label={field.label}
            {...getFieldProps(field)}
          />
        );

        break;
      case "radio":
        formField = (
          <Radio
            key={field.name}
            name={field.name}
            control={control}
            label={field.label}
            {...getFieldProps(field)}
          />
        );

        break;
      case "switch":
        formField = (
          <SwitchToggle
            key={field.name}
            name={field.name}
            control={control}
            label={field.label}
            {...getFieldProps(field)}
          />
        );

        break;
      case "toggle-buttons":
        formField = (
          <ToggleButtons
            key={field.name}
            name={field.name}
            control={control}
            label={field.label}
            options={field.options}
            {...getFieldProps(field)}
          />
        );

        break;
      case "multi-select":
        formField = (
          <MultiSelect
            key={field.name}
            name={field.name}
            control={control}
            label={field.label}
            options={field.options}
            {...getFieldProps(field)}
          />
        );

        break;
      case "multi-select-response":
        formField = (
          <MultiSelectResponse
            key={field.name}
            name={field.name}
            label={field.label}
            options={field.options}
            schema={field.schema}
            control={control}
            {...getFieldProps(field)}
          />
        );

        break;
      case "conditional-response":
        formField = (
          <ConditionalResponse
            key={field.name}
            name={field.name}
            label={field.label}
            schema={field.fieldProps?.schema}
            control={control}
            {...getFieldProps(field)}
          />
        );

        break;
      case "file-input":
        formField = (
          <FileInput
            key={field.name}
            name={field.name}
            control={control}
            {...getFieldProps(field)}
          />
        );

        break;
      case "custom": {
        if (!field.component) break;
        const Component = field.component as React.ComponentType<any>;
        formField = (
          <Component
            key={`${field.name}-container`}
            name={field.name}
            label={field.label}
            control={control}
            {...field.fieldProps}
          />
        );

        break;
      }
      case "group-input": {
        formField = (
          <Stack
            direction={field.fieldProps?.direction || "row"}
            spacing={field.fieldProps?.spacing || { xs: 2, md: 1 }}
          >
            {field.group.map((field) => (
              <FormFieldComponent field={field} control={control} />
            ))}
          </Stack>
        );

        break;
      }
      default:
        break;
    }

    const error = getFormErrorMessage(formState, field.name);

    return (
      <FieldWrapper key={field.name} field={field}>
        {field.type !== "group-input" && !field.label && error && (
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        )}
        {formField}
      </FieldWrapper>
    );
  };

  const formField = getField(field);

  return (
    <>
      {field.dividerTop && renderDivider()}
      {formField}
      {field.dividerBottom && renderDivider()}
    </>
  );
};

export default FormFieldComponent;
